/**
 * PSV-1904 — in-canvas chat feed.
 *
 * The first iteration rendered messages as a DOM layer; DOM ignores the
 * Phaser FIT scale and always paints ABOVE the canvas, so messages
 * landed on top of the stat panel and even the shop modal (PO screenshot
 * 2026-07-27). The feed is now Phaser HUD: it scales with the scene,
 * sits BELOW modals (depth), and is anchored right of the stat panel.
 * Only the text INPUT stays DOM (see ChatOverlay) — it's transient and
 * intentionally modal while typing.
 */

import Phaser from 'phaser';

const FEED_X = 290; // right of the stat panel (256 + margins)
const FEED_BOTTOM_MARGIN = 34; // just above the controls bar
const MAX_MESSAGES = 6;
const MESSAGE_TTL_MS = 8_000;
const FADE_MS = 600;
const LINE_GAP = 3;
const WRAP_WIDTH = 460;
const DEPTH = 15; // below panels (18) and far below modals (30)

interface FeedEntry {
  text: Phaser.GameObjects.Text;
  expiresAt: number;
  fading: boolean;
}

export class ChatFeed {
  private readonly scene: Phaser.Scene;
  private readonly entries: FeedEntry[] = [];
  private destroyed = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  add(name: string, message: string, isMine: boolean): void {
    if (this.destroyed) return;
    const text = this.scene.add
      .text(FEED_X, 0, `${name}: ${message}`, {
        fontSize: '12px',
        color: isMine ? '#9dffbe' : '#dfe9f5',
        backgroundColor: 'rgba(6, 10, 20, 0.55)',
        padding: { x: 6, y: 2 },
        wordWrap: { width: WRAP_WIDTH },
      })
      .setScrollFactor(0)
      .setDepth(DEPTH);
    this.entries.push({ text, expiresAt: Date.now() + MESSAGE_TTL_MS, fading: false });
    while (this.entries.length > MAX_MESSAGES) {
      this.entries.shift()?.text.destroy();
    }
    this.layout();
  }

  /** Per-frame: TTL fade-out (called from ArenaController.update). */
  update(): void {
    if (this.destroyed || this.entries.length === 0) return;
    const now = Date.now();
    let removed = false;
    for (const entry of [...this.entries]) {
      if (!entry.fading && now >= entry.expiresAt) {
        entry.fading = true;
        this.scene.tweens.add({
          targets: entry.text,
          alpha: 0,
          duration: FADE_MS,
          onComplete: () => entry.text.destroy(),
        });
        this.entries.splice(this.entries.indexOf(entry), 1);
        removed = true;
      }
    }
    if (removed) this.layout();
  }

  setVisible(visible: boolean): void {
    for (const entry of this.entries) entry.text.setVisible(visible);
  }

  destroy(): void {
    this.destroyed = true;
    for (const entry of this.entries) entry.text.destroy();
    this.entries.length = 0;
  }

  // ---------- private ----------

  /** Stack bottom-up from the controls bar. */
  private layout(): void {
    const cam = this.scene.cameras.main;
    let y = cam.height - FEED_BOTTOM_MARGIN;
    for (let i = this.entries.length - 1; i >= 0; i--) {
      const entry = this.entries[i]!;
      y -= entry.text.height + LINE_GAP;
      entry.text.setPosition(FEED_X, y);
    }
  }
}
